import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Plus, Edit, Trash2, FileText, Tag, Lock, ExternalLink, LogOut, LayoutDashboard } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('admin_token');

  const checkAuth = useCallback(async () => {
    const token = getToken();
    if (!token) { navigate('/admin'); return; }
    try {
      const res = await fetch(`${API}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setUser(await res.json());
    } catch { localStorage.removeItem('admin_token'); navigate('/admin'); }
  }, [navigate]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => {
    if (!user) return;
    const token = getToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    Promise.all([
      fetch(`${API}/api/posts`, { headers }).then(r => r.json()),
      fetch(`${API}/api/categories`, { headers }).then(r => r.json())
    ]).then(([p, c]) => { setPosts(p); setCategories(c); }).catch(() => {});
  }, [user]);

  const deletePost = async (id) => {
    if (!window.confirm('Izbriši objavo?')) return;
    await fetch(`${API}/api/posts/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${getToken()}` } });
    setPosts(posts.filter(p => p.id !== id));
  };

  const logout = () => { localStorage.removeItem('admin_token'); navigate('/admin'); };
  const getCatName = (id) => categories.find(c => c.id === id)?.name || '—';

  if (!user) return null;

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-stone-200 flex flex-col fixed h-full">
        <div className="p-4 border-b border-stone-100">
          <Link to="/" className="font-serif text-lg text-violet-900 font-bold">Val Dotika</Link>
          <p className="text-stone-500 text-xs mt-0.5">Admin panel</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <Link to="/admin/dashboard" className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-violet-700 bg-violet-50 rounded-lg"><LayoutDashboard size={16} />Nadzorna plošča</Link>
          <Link to="/admin/categories" className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-600 hover:bg-stone-50 rounded-lg"><Tag size={16} />Kategorije</Link>
          <Link to="/admin/change-password" className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-600 hover:bg-stone-50 rounded-lg"><Lock size={16} />Spremeni geslo</Link>
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-600 hover:bg-stone-50 rounded-lg"><ExternalLink size={16} />Poglej stran</a>
        </nav>
        <div className="p-3 border-t border-stone-100">
          <button onClick={logout} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg w-full"><LogOut size={16} />Odjava</button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 ml-56 p-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-serif text-3xl text-stone-800 font-bold" data-testid="dashboard-title">Objave</h1>
              <p className="text-stone-500 text-sm">{posts.length} {posts.length === 1 ? 'objava' : 'objav'}</p>
            </div>
            <Link to="/admin/posts/new">
              <Button className="bg-violet-500 hover:bg-violet-600 text-white rounded-lg" data-testid="new-post-btn"><Plus size={16} className="mr-1" />Nova objava</Button>
            </Link>
          </div>

          {posts.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-stone-100">
              <FileText size={40} className="mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500">Ni še nobene objave.</p>
              <Link to="/admin/posts/new"><Button className="mt-3 bg-violet-500 hover:bg-violet-600 text-white rounded-lg"><Plus size={16} className="mr-1" />Ustvari prvo objavo</Button></Link>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              <table className="w-full">
                <thead><tr className="bg-stone-50 border-b border-stone-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Naslov</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Kategorija</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Datum</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Dejanja</th>
                </tr></thead>
                <tbody>
                  {posts.map(post => (
                    <tr key={post.id} className="border-b border-stone-50 hover:bg-stone-50" data-testid={`post-row-${post.id}`}>
                      <td className="px-4 py-3"><p className="text-sm font-medium text-stone-800 truncate max-w-xs">{post.title}</p></td>
                      <td className="px-4 py-3"><span className="text-xs text-stone-500">{getCatName(post.category_id)}</span></td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${post.status === 'published' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                          {post.status === 'published' ? 'Objavljeno' : 'Osnutek'}
                        </span>
                      </td>
                      <td className="px-4 py-3"><span className="text-xs text-stone-400">{new Date(post.created_at).toLocaleDateString('sl-SI')}</span></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link to={`/admin/posts/${post.id}/edit`}><Button variant="ghost" size="sm" className="text-stone-500 hover:text-violet-600"><Edit size={14} /></Button></Link>
                          <Button variant="ghost" size="sm" className="text-stone-500 hover:text-red-600" onClick={() => deletePost(post.id)}><Trash2 size={14} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
